import { useState, useEffect, createContext, useContext } from "react";
import { addWeeks, differenceInWeeks, differenceInDays, addDays } from "date-fns";
import { safeParseJSON, safeSaveJSONAsync, isObject } from "@/lib/safeLocalStorage";

export interface PregnancyData {
  isPregnant: boolean;
  dueDate?: string;
  conceptionDate?: string;
  lastPeriodDate?: string;
  weeksPregnant?: number;
  trimester?: 1 | 2 | 3;
}

interface PregnancyContextType {
  pregnancyData: PregnancyData;
  setPregnancyMode: (isPregnant: boolean) => void;
  setDueDate: (date: string) => void;
  setLastPeriodDate: (date: string) => void;
  getWeeksAndDays: () => { weeks: number; days: number };
  getTrimester: () => 1 | 2 | 3 | null;
  getDaysUntilDue: () => number | null;
  getBabySize: () => { fruit: string; length: string; weight: string } | null;
}

const STORAGE_KEY = "pregnancyData";

const defaultData: PregnancyData = { isPregnant: false };

const isValidPregnancyData = (data: unknown): data is PregnancyData => {
  if (!isObject(data)) return false;
  const obj = data as Record<string, unknown>;
  return typeof obj.isPregnant === "boolean";
};

const PregnancyContext = createContext<PregnancyContextType | undefined>(undefined);

const babySizeByWeek: Record<number, { fruit: string; length: string; weight: string }> = {
  4: { fruit: "Poppy seed", length: "0.04 in", weight: "< 1 g" },
  5: { fruit: "Sesame seed", length: "0.05 in", weight: "< 1 g" },
  6: { fruit: "Lentil", length: "0.08 in", weight: "< 1 g" },
  7: { fruit: "Blueberry", length: "0.3 in", weight: "< 1 g" },
  8: { fruit: "Raspberry", length: "0.6 in", weight: "1 g" },
  9: { fruit: "Cherry", length: "0.9 in", weight: "2 g" },
  10: { fruit: "Kumquat", length: "1.2 in", weight: "4 g" },
  11: { fruit: "Fig", length: "1.6 in", weight: "7 g" },
  12: { fruit: "Lime", length: "2.1 in", weight: "14 g" },
  13: { fruit: "Lemon", length: "2.9 in", weight: "23 g" },
  14: { fruit: "Peach", length: "3.4 in", weight: "43 g" },
  15: { fruit: "Apple", length: "4 in", weight: "70 g" },
  16: { fruit: "Avocado", length: "4.6 in", weight: "100 g" },
  17: { fruit: "Pear", length: "5.1 in", weight: "140 g" },
  18: { fruit: "Bell pepper", length: "5.6 in", weight: "190 g" },
  19: { fruit: "Mango", length: "6 in", weight: "240 g" },
  20: { fruit: "Banana", length: "6.5 in", weight: "300 g" },
  21: { fruit: "Carrot", length: "10.5 in", weight: "360 g" },
  22: { fruit: "Papaya", length: "11 in", weight: "430 g" },
  23: { fruit: "Grapefruit", length: "11.4 in", weight: "500 g" },
  24: { fruit: "Cantaloupe", length: "11.8 in", weight: "600 g" },
  25: { fruit: "Cauliflower", length: "13.6 in", weight: "660 g" },
  26: { fruit: "Lettuce", length: "14 in", weight: "760 g" },
  27: { fruit: "Cabbage", length: "14.4 in", weight: "875 g" },
  28: { fruit: "Eggplant", length: "14.8 in", weight: "1 kg" },
  29: { fruit: "Butternut squash", length: "15.2 in", weight: "1.15 kg" },
  30: { fruit: "Cucumber", length: "15.7 in", weight: "1.3 kg" },
  31: { fruit: "Coconut", length: "16.2 in", weight: "1.5 kg" },
  32: { fruit: "Jicama", length: "16.7 in", weight: "1.7 kg" },
  33: { fruit: "Pineapple", length: "17.2 in", weight: "1.9 kg" },
  34: { fruit: "Honeydew melon", length: "17.7 in", weight: "2.1 kg" },
  35: { fruit: "Spaghetti squash", length: "18.2 in", weight: "2.4 kg" },
  36: { fruit: "Romaine lettuce", length: "18.7 in", weight: "2.6 kg" },
  37: { fruit: "Swiss chard", length: "19.1 in", weight: "2.9 kg" },
  38: { fruit: "Leek", length: "19.6 in", weight: "3 kg" },
  39: { fruit: "Watermelon", length: "20 in", weight: "3.3 kg" },
  40: { fruit: "Pumpkin", length: "20.2 in", weight: "3.5 kg" },
};

export const PregnancyProvider = ({ children }: { children: React.ReactNode }) => {
  const [pregnancyData, setPregnancyData] = useState<PregnancyData>(() => {
    return safeParseJSON(STORAGE_KEY, defaultData, isValidPregnancyData);
  });

  useEffect(() => {
    void safeSaveJSONAsync(STORAGE_KEY, pregnancyData);
  }, [pregnancyData]);

  const setPregnancyMode = (isPregnant: boolean) => {
    setPregnancyData((prev) => ({ ...prev, isPregnant }));
  };

  const setDueDate = (date: string) => {
    // Calculate conception date (approximately 38 weeks before due date)
    const dueDateObj = new Date(date);
    const conceptionDate = addWeeks(dueDateObj, -38);
    const lastPeriodDate = addDays(conceptionDate, -14); // LMP is ~2 weeks before conception
    
    setPregnancyData((prev) => ({
      ...prev,
      dueDate: date,
      conceptionDate: conceptionDate.toISOString(),
      lastPeriodDate: lastPeriodDate.toISOString(),
    }));
  };

  const setLastPeriodDate = (date: string) => {
    // Calculate due date (40 weeks from LMP)
    const lmpDate = new Date(date);
    const dueDate = addWeeks(lmpDate, 40);
    const conceptionDate = addWeeks(lmpDate, 2);
    
    setPregnancyData((prev) => ({
      ...prev,
      lastPeriodDate: date,
      dueDate: dueDate.toISOString(),
      conceptionDate: conceptionDate.toISOString(),
    }));
  };

  const getWeeksAndDays = () => {
    if (!pregnancyData.lastPeriodDate) return { weeks: 0, days: 0 };
    
    const lmpDate = new Date(pregnancyData.lastPeriodDate);
    const today = new Date();
    const totalDays = differenceInDays(today, lmpDate);
    const weeks = Math.floor(totalDays / 7);
    const days = totalDays % 7;
    
    return { weeks: Math.max(0, Math.min(weeks, 42)), days: Math.max(0, days) };
  };

  const getTrimester = (): 1 | 2 | 3 | null => {
    const { weeks } = getWeeksAndDays();
    if (weeks < 1) return null;
    if (weeks <= 12) return 1;
    if (weeks <= 27) return 2;
    return 3;
  };

  const getDaysUntilDue = (): number | null => {
    if (!pregnancyData.dueDate) return null;
    const dueDate = new Date(pregnancyData.dueDate);
    const today = new Date();
    return Math.max(0, differenceInDays(dueDate, today));
  };

  const getBabySize = () => {
    const { weeks } = getWeeksAndDays();
    if (weeks < 4 || weeks > 40) return null;
    return babySizeByWeek[weeks] || babySizeByWeek[Math.min(weeks, 40)];
  };

  return (
    <PregnancyContext.Provider
      value={{
        pregnancyData,
        setPregnancyMode,
        setDueDate,
        setLastPeriodDate,
        getWeeksAndDays,
        getTrimester,
        getDaysUntilDue,
        getBabySize,
      }}
    >
      {children}
    </PregnancyContext.Provider>
  );
};

export const usePregnancyMode = () => {
  const context = useContext(PregnancyContext);
  if (context === undefined) {
    throw new Error("usePregnancyMode must be used within a PregnancyProvider");
  }
  return context;
};
